import { useState, useEffect, useCallback } from 'react';
import { Card, Empty, Tag, Spin, Modal, Form, Input, Space } from 'antd';
import {
  ExclamationCircleOutlined,
  FileOutlined,
  CommentOutlined,
  CheckCircleOutlined,
  PlusOutlined,
  ArrowRightOutlined
} from '@ant-design/icons';
import { Button, Text } from '@bancobarigui/barihaus';

import { inspectionIssuesService } from '@/services/inspectionIssues.service';
import { CreateIssueModal } from '@/components/CreateIssueModal';
import { usePermissions } from '@/shared/hooks/usePermissions';
import { useAsyncOperation } from '@/shared/hooks/useAsyncOperation';
import { InspectionIssue } from '@/services/types/inspectionIssues.types';
import { InspectionIssueStatus } from '@/services/types/inspection.types';

interface InspectionIssuesProps {
  inspection: any;
}

const TARGET_ROLE_LABELS: Record<string, string> = {
  Provider: 'Prestadora',
  Bank: 'Banco',
  Inspector: 'Inspetor',
};

const TARGET_ROLE_COLORS: Record<string, string> = {
  Provider: 'purple',
  Bank: 'geekblue',
  Inspector: 'cyan',
};

export function InspectionIssues({ inspection }: InspectionIssuesProps) {
  const [issues, setIssues] = useState<InspectionIssue[]>([]);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isResolveModalOpen, setIsResolveModalOpen] = useState(false);
  const [isCommentModalOpen, setIsCommentModalOpen] = useState(false);
  const [selectedIssue, setSelectedIssue] = useState<InspectionIssue | null>(null);
  const [resolveForm] = Form.useForm();
  const [commentForm] = Form.useForm();

  const { isInspectorUser, isProviderUser, isBankUser } = usePermissions();

  // Determina se o usuário pode criar pendências
  const canCreateIssue = isInspectorUser() || isProviderUser() || isBankUser();

  // Determina se o usuário pode resolver uma issue específica (baseado no targetRole)
  const canResolveIssue = (issue: InspectionIssue) => {
    if (issue.status === InspectionIssueStatus.Resolvida) return false;
    const target = issue.targetRole || 'Provider';
    if (target === 'Provider' && isProviderUser()) return true;
    if (target === 'Bank' && isBankUser()) return true;
    return false;
  };

  // Todos podem comentar (Provider, Bank, Inspector)
  const canCommentIssue = (issue: InspectionIssue) => {
    return issue.status !== InspectionIssueStatus.Resolvida;
  };

  const { loading, execute: executeLoadIssues } = useAsyncOperation<InspectionIssue[]>({
    errorMessage: 'Erro ao carregar pendências',
  });

  const loadIssues = useCallback(async () => {
    const itemId = inspection.items?.[0]?.id;
    if (!itemId) {
      setIssues([]);
      return;
    }

    await executeLoadIssues(async () => {
      const data = await inspectionIssuesService.getIssuesByInspection({
        inspectionItemId: itemId
      });
      setIssues(data);
      return data;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inspection.items]);

  const { loading: resolving, execute: executeResolve } = useAsyncOperation({
    successMessage: 'Pendência resolvida com sucesso!',
    errorMessage: 'Erro ao resolver pendência',
    onSuccess: () => {
      resolveForm.resetFields();
      setIsResolveModalOpen(false);
      setSelectedIssue(null);
      loadIssues();
    },
  });

  const { loading: commenting, execute: executeComment } = useAsyncOperation({
    successMessage: 'Comentário adicionado com sucesso!',
    errorMessage: 'Erro ao adicionar comentário',
    onSuccess: () => {
      commentForm.resetFields();
      setIsCommentModalOpen(false);
      setSelectedIssue(null);
      loadIssues();
    },
  });

  useEffect(() => {
    if (inspection?.id) {
      loadIssues();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inspection?.id]);

  const handleCreateSuccess = () => {
    loadIssues();
  };

  const handleResolve = (issue: InspectionIssue) => {
    setSelectedIssue(issue);
    setIsResolveModalOpen(true);
  };

  const handleComment = (issue: InspectionIssue) => {
    setSelectedIssue(issue);
    setIsCommentModalOpen(true);
  };

  const handleResolveSubmit = async (values: { resolutionNotes: string }) => {
    if (!selectedIssue) return;

    await executeResolve(async () => {
      await inspectionIssuesService.resolveIssue(selectedIssue.id, {
        resolutionNotes: values.resolutionNotes
      });
    });
  };

  const handleCommentSubmit = async (values: { comment: string; isInternal: boolean }) => {
    if (!selectedIssue) return;

    await executeComment(async () => {
      await inspectionIssuesService.addComment(selectedIssue.id, {
        comment: values.comment,
        isInternal: values.isInternal || false
      });
    });
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return 'Data não disponível';

    try {
      const date = new Date(dateString);

      if (isNaN(date.getTime())) {
        return 'Data inválida';
      }

      return date.toLocaleString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return 'Data inválida';
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-20">
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <Text strong className="text-lg">Pendências da Inspeção</Text>
          <Text className="text-gray-500 block">
            Visualize e gerencie as pendências reportadas para esta inspeção
          </Text>
        </div>

        {canCreateIssue && (
          <Button
            buttonColor="primary"
            icon={<PlusOutlined />}
            onClick={() => setIsCreateModalOpen(true)}
          >
            Nova Pendência
          </Button>
        )}
      </div>

      {issues.length === 0 ? (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description="Nenhuma pendência encontrada"
        />
      ) : (
        <div className="space-y-4">
          {issues.map((issue) => (
            <Card key={issue.id} className="shadow-sm">
              <div className="space-y-4">
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2 flex-wrap">
                      <ExclamationCircleOutlined className={issue.status === InspectionIssueStatus.Resolvida ? "text-green-500" : "text-orange-500"} />
                      <Text strong className="text-lg">{issue.title}</Text>
                      <Tag color={issue.status === InspectionIssueStatus.Resolvida ? 'green' : 'orange'}>
                        {issue.statusDescription}
                      </Tag>
                      <Tag color="blue">
                        {issue.issueTypeName || issue.typeDescription}
                      </Tag>
                      {issue.targetRole && (
                        <Tag color={TARGET_ROLE_COLORS[issue.targetRole] || 'default'} icon={<ArrowRightOutlined />}>
                          {TARGET_ROLE_LABELS[issue.targetRole] || issue.targetRole}
                        </Tag>
                      )}
                    </div>

                    <Text className="text-gray-700 block mb-3">
                      {issue.description}
                    </Text>

                    <div className="flex items-center gap-4 text-sm text-gray-500">
                      <span>Criado por: <strong>{issue.reportedByName}</strong></span>
                      <span>{formatDate(issue.reportedAt)}</span>
                      {issue.attachments.length > 0 && (
                        <span className="flex items-center gap-1">
                          <FileOutlined />
                          {issue.attachments.length} anexo(s)
                        </span>
                      )}
                    </div>
                  </div>

                  <Space>
                    {canResolveIssue(issue) && (
                      <Button
                        buttonColor="primary"
                        icon={<CheckCircleOutlined />}
                        onClick={() => handleResolve(issue)}
                        size="small"
                      >
                        Resolver
                      </Button>
                    )}
                    {canCommentIssue(issue) && (
                      <Button
                        icon={<CommentOutlined />}
                        onClick={() => handleComment(issue)}
                        size="small"
                      >
                        Comentar
                      </Button>
                    )}
                  </Space>
                </div>

                {issue.status === InspectionIssueStatus.Resolvida && (
                  <div className="bg-green-50 border-l-4 border-green-400 p-4 rounded">
                    <div className="flex items-center gap-2 mb-2">
                      <CheckCircleOutlined className="text-green-600" />
                      <Text strong className="text-green-800">Resolvida</Text>
                    </div>
                    <Text className="text-green-700 block mb-2">
                      {issue.resolutionNotes}
                    </Text>
                    <Text className="text-sm text-green-600">
                      Resolvida por: <strong>{issue.resolvedByName}</strong> em {formatDate(issue.resolvedAt!)}
                    </Text>
                  </div>
                )}

                {issue.comments && issue.comments.length > 0 && (
                  <div className="border-t pt-4">
                    <Text strong className="block mb-3">Comentários ({issue.comments.length}):</Text>
                    <div className="space-y-3">
                      {issue.comments.map((comment, index) => (
                        <div key={comment.id || index} className="bg-gray-50 p-3 rounded">
                          <Text className="text-gray-700 block mb-2">
                            {comment.comment}
                          </Text>
                          <div className="flex items-center gap-2 text-sm text-gray-500">
                            <span>
                              <strong>{comment.commentedByName || 'Usuário não identificado'}</strong>
                            </span>
                            <span>•</span>
                            <span>{formatDate(comment.commentedAt)}</span>
                            {comment.isInternal && (
                              <Tag color="red">Interno</Tag>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {issue.attachments.length > 0 && (
                  <div className="border-t pt-4">
                    <Text strong className="block mb-3">Anexos:</Text>
                    <div className="grid grid-cols-2 gap-3">
                      {issue.attachments.map((attachment, index) => (
                        <div key={index} className="flex items-center gap-2 p-2 bg-gray-50 rounded">
                          <FileOutlined className="text-blue-500" />
                          <div className="flex-1 min-w-0">
                            <Text className="text-sm font-medium block truncate">
                              {attachment.fileName}
                            </Text>
                            <Text className="text-xs text-gray-500">
                              {(attachment.fileSizeBytes / 1024).toFixed(1)} KB
                            </Text>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </Card>
          ))}
        </div>
      )}

      <CreateIssueModal
        open={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        inspectionItemId={inspection.items?.[0]?.id || ''}
        onSuccess={handleCreateSuccess}
      />

      {isResolveModalOpen && (
        <Modal
          title="Resolver Pendência"
          open={isResolveModalOpen}
          onCancel={() => {
            setIsResolveModalOpen(false);
            setSelectedIssue(null);
            resolveForm.resetFields();
          }}
          footer={null}
        >
          <Form
            form={resolveForm}
            layout="vertical"
            onFinish={handleResolveSubmit}
          >
            <Form.Item
              name="resolutionNotes"
              label="Notas de Resolução"
              rules={[
                { required: true, message: 'Por favor, descreva como a pendência foi resolvida' },
                { min: 10, message: 'A descrição deve ter pelo menos 10 caracteres' }
              ]}
            >
              <Input.TextArea
                placeholder="Descreva como a pendência foi resolvida..."
                rows={4}
                maxLength={500}
                showCount
              />
            </Form.Item>

            <Form.Item className="mb-0">
              <div className="flex gap-3 justify-end">
                <Button onClick={() => setIsResolveModalOpen(false)}>
                  Cancelar
                </Button>
                <Button
                  buttonColor="primary"
                  htmlType="submit"
                  loading={resolving}
                >
                  Resolver Pendência
                </Button>
              </div>
            </Form.Item>
          </Form>
        </Modal>
      )}

      {isCommentModalOpen && (
        <Modal
          title="Adicionar Comentário"
          open={isCommentModalOpen}
          onCancel={() => {
            setIsCommentModalOpen(false);
            setSelectedIssue(null);
            commentForm.resetFields();
          }}
          footer={null}
        >
          <Form
            form={commentForm}
            layout="vertical"
            onFinish={handleCommentSubmit}
          >
            <Form.Item
              name="comment"
              label="Comentário"
              rules={[
                { required: true, message: 'Por favor, digite um comentário' },
                { min: 5, message: 'O comentário deve ter pelo menos 5 caracteres' }
              ]}
            >
              <Input.TextArea
                placeholder="Digite seu comentário sobre esta pendência..."
                rows={4}
                maxLength={500}
                showCount
              />
            </Form.Item>

            <Form.Item className="mb-0">
              <div className="flex gap-3 justify-end">
                <Button onClick={() => setIsCommentModalOpen(false)}>
                  Cancelar
                </Button>
                <Button
                  buttonColor="primary"
                  htmlType="submit"
                  loading={commenting}
                >
                  Adicionar Comentário
                </Button>
              </div>
            </Form.Item>
          </Form>
        </Modal>
      )}
    </div>
  );
}
